import React, { useCallback, useEffect, useState } from 'react'
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js'
import './App.scss'
import { TRPCProvider } from './Components/TRPCProvider'
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { DataProvider } from './DataContext'
import UserAuthorization from './Components/AuthComponent'
import { runtimeConfig } from './runtimeConfig'

const INACTIVITY_TIMEOUT = 60 * 60 * 1000

const MainContent: React.FC = () => {
  const [ascendProjectId, setAscendProjectId] = useState<string | null>(null)
  const [lastActivity, setLastActivity] = useState(Date.now())
  const { instance, inProgress } = useMsal()

  // Handle user activity
  const resetTimer = useCallback(() => {
    setLastActivity(Date.now())
  }, [])

  // Sign out the user
  const handleSignOut = useCallback(
    (_interval: any) => {
      instance.logoutRedirect()
      resetTimer()
    },
    [instance],
  )

  useEffect(() => {
    // Parse the current URL's search parameters
    const params = new URLSearchParams(window.location.search)

    // Get the value of the 'ascend_project_id' parameter
    let tempProjectId = params.get('ascend_project_id')
    if (tempProjectId === null) {
      tempProjectId = ''
    }
    setAscendProjectId(tempProjectId)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      if (Date.now() - lastActivity > INACTIVITY_TIMEOUT) {
        handleSignOut(interval)
      }
    }, 1000) // Every second check
    return () => clearInterval(interval)
  }, [lastActivity, handleSignOut])

  // Event listeners for detecting user activity
  useEffect(() => {
    window.addEventListener('mousemove', resetTimer)
    window.addEventListener('keypress', resetTimer)
    window.addEventListener('click', resetTimer)

    return () => {
      window.removeEventListener('mousemove', resetTimer)
      window.removeEventListener('keypress', resetTimer)
      window.removeEventListener('click', resetTimer)
    }
  }, [resetTimer])

  // Avoid signing out during Auth process
  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      resetTimer()
    }
  }, [inProgress, resetTimer])

  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return <UserAuthorization /> // Prevents blank screen during MSAL initialization
  }

  return (
    <DataProvider>
      <div className='App'>
        {ascendProjectId ? (
          <AuthenticatedTemplate>
            <TRPCProvider>
              <UserAuthorization />
            </TRPCProvider>
          </AuthenticatedTemplate>
        ) : (
          <>
            <UnauthenticatedTemplate />
            <UserAuthorization />
          </>
        )}
      </div>
    </DataProvider>
  )
}

const App: React.FC = () => {
  const { instance, inProgress } = useMsal()
  const aiPlugin = useAppInsightsContext()

  useEffect(() => {
    console.log("[APP] App is mounted (debug only)");
  }, []);

  /**
   * Log environment variables for debugging
   * */
  useEffect(() => {
    console.log('Environment Variables:', {
      VITE_ASCEND_1_1_URL: runtimeConfig.VITE_ASCEND_1_1_URL,
      VITE_ASCEND_CHAT_URL: runtimeConfig.VITE_ASCEND_CHAT_URL,
      VITE_CLIENT_ID: runtimeConfig.VITE_CLIENT_ID,
      VITE_TENANT_ID: runtimeConfig.VITE_TENANT_ID,
      VITE_KDD_API: runtimeConfig.VITE_KDD_API,
      VITE_DOWNLOAD_PLUGIN_URL: runtimeConfig.VITE_DOWNLOAD_PLUGIN_URL,
      VITE_USER_GUIDE_URL: runtimeConfig.VITE_USER_GUIDE_URL,
      VITE_ENV: runtimeConfig.VITE_ENV,
      VITE_APPINSIGHTS_INSTRUMENTATION_KEY: runtimeConfig.VITE_APPINSIGHTS_INSTRUMENTATION_KEY,
    })
  }, [])

  /**
   * Setup app insights when App is mounted
   * */
  useEffect(() => {
    if (!aiPlugin?.core) {
      console.warn('App Insights core not initialized — skipping telemetry initializer')
      return
    }
    aiPlugin.core.addTelemetryInitializer((env) => {
      try {
        env.tags ??= {}
        env.tags['ai.cloud.role'] = 'workshop-assistant-frontend'

        env.data ??= {}
        env.data.baseData ??= {}
        env.data.baseData.properties ??= {}

        // App metadata
        env.data.baseData.properties['appName'] = 'WORKSHOP_ASSISTANT_FRONTEND'

        // User context
        const accounts = instance.getAllAccounts()
        const account = accounts?.[0]

        if (account?.name) {
          env.data.baseData.properties['userName'] = account.name
        }
        if (account?.username) {
          env.data.baseData.properties['userEmail'] = account.username
        }
        if (account?.localAccountId) {
          env.data.baseData.properties['userId'] = account.localAccountId
        }
      } catch (error) {
        console.warn('Failed to set AI telemetry context:', error)
      }
    })
  }, [instance, aiPlugin])

  useEffect(() => {
    if (inProgress === InteractionStatus.None && instance.getAllAccounts().length === 0) {
      instance.loginRedirect()
    }
  }, [instance, inProgress])

  return <MainContent />
}

export default App
